import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Zap, Play, Settings, Trash2, ChevronDown, ChevronUp, Smartphone, StopCircle, RefreshCw, Edit3, Check, X } from 'lucide-react';
import AddToQueueModal from '../components/modals/AddToQueueModal';
import WorkflowBuilder from '../components/workflow/WorkflowBuilder';
import SharedPageHeader from '../components/layout/PageHeader';
import { getLocalProfilePicUrl } from '../config/imageUrls';
import { apiUrl } from '../config/api';

const PageContainer = styled.div`
  padding: 20px;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--bg);

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    padding: 12px;
  }
`;

const WorkflowsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  flex: 1;
  overflow: hidden;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    overflow: visible;
    gap: 14px;
  }
`;

const WorkflowColumn = styled.div`
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg) 100%);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(165, 145, 239, 0.4);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => {
      if (props.$workflowId === 1) return 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.6), transparent)';
      if (props.$workflowId === 2) return 'linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.6), transparent)';
      if (props.$workflowId === 3) return 'linear-gradient(90deg, transparent, rgba(245, 158, 11, 0.6), transparent)';
      return 'linear-gradient(90deg, transparent, rgba(239, 68, 68, 0.6), transparent)';
    }};
  }
`;

const ColumnHeader = styled.div`
  padding: 20px;
  background: var(--bg);
  border-bottom: 1px solid var(--card-border);
`;

const ColumnTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const WorkflowIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => {
    if (props.$workflowId === 1) return 'linear-gradient(135deg, var(--info), var(--info))'; // A = Bleu
    if (props.$workflowId === 2) return 'linear-gradient(135deg, var(--success), var(--success))'; // B = Vert
    if (props.$workflowId === 3) return 'linear-gradient(135deg, var(--warning), var(--warning))'; // C = Orange
    return 'linear-gradient(135deg, var(--error), var(--error))'; // D = Rouge
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 700;
`;

const ColumnMeta = styled.div`
  font-size: 13px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled.button`
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 10px;
  color: ${props => {
    if (props.$workflowId === 1) return 'var(--info)';
    if (props.$workflowId === 2) return 'var(--success)';
    if (props.$workflowId === 3) return 'var(--warning)';
    return 'var(--error)';
  }};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;

  &:hover {
    background: ${props => {
      if (props.$workflowId === 1) return 'rgba(59, 130, 246, 0.15)';
      if (props.$workflowId === 2) return 'rgba(16, 185, 129, 0.15)';
      if (props.$workflowId === 3) return 'rgba(245, 158, 11, 0.15)';
      return 'rgba(239, 68, 68, 0.15)';
    }};
    border-color: ${props => {
      if (props.$workflowId === 1) return 'var(--info)';
      if (props.$workflowId === 2) return 'var(--success)';
      if (props.$workflowId === 3) return 'var(--warning)';
      return 'var(--error)';
    }};
    transform: scale(1.1);
  }
`;

const ExpandButton = styled.button`
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  width: 36px;
  height: 36px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--card-bg);
    border-color: var(--primary);
    color: var(--text-primary);
    transform: scale(1.1);
  }
`;

const ProcessesList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(165, 145, 239, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ProcessCard = styled.div`
  background: var(--bg);
  border: 1px solid ${props => props.$selected ? (
    props.$workflowId === 1 ? 'var(--info)' : 
    props.$workflowId === 2 ? 'var(--success)' : 
    props.$workflowId === 3 ? 'var(--warning)' : 'var(--error)'
  ) : 'var(--card-border)'};
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--card-bg);
    border-color: ${props => 
      props.$workflowId === 1 ? 'var(--info)' : 
      props.$workflowId === 2 ? 'var(--success)' : 
      props.$workflowId === 3 ? 'var(--warning)' : 'var(--error)'
    };
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);
  }
`;

const ProcessHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ProcessProfilePic = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--card-border);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .fallback {
    font-size: 16px;
    font-weight: 700;
    color: var(--text-primary);
  }
`;

const ProcessInfo = styled.div`
  flex: 1;
`;

const ProcessUsername = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const ProcessDevice = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #aaa;
`;

const ProcessStatus = styled.div`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    if (props.$status === 'running') return 'linear-gradient(135deg, var(--warning), var(--warning))';
    if (props.$status === 'completed') return 'linear-gradient(135deg, var(--success), #16a34a)';
    return 'linear-gradient(135deg, var(--text-muted), var(--text-muted))';
  }};
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const ProcessProgress = styled.div`
  margin-top: 12px;
`;



const ProgressText = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #aaa;
  display: flex;
  justify-content: space-between;
  
  &:first-child {
    font-size: 14px;
    font-weight: 600;
  }
`;



const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #888;
  padding: 40px 20px;
`;

const EmptyIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #666;
`;

const EmptyText = styled.div`
  font-size: 14px;
  text-align: center;
`;

const TaskQueue = styled.div`
  padding: 12px 16px;
  background: var(--bg);
  border-top: 1px solid var(--card-border);
  border-bottom: 1px solid var(--card-border);
  margin-bottom: 12px;
`;

const TaskQueueHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TaskQueueTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TaskQueueCount = styled.span`
  background: ${props => {
    if (props.$workflowId === 1) return 'rgba(59, 130, 246, 0.2)';
    if (props.$workflowId === 2) return 'rgba(16, 185, 129, 0.2)';
    if (props.$workflowId === 3) return 'rgba(245, 158, 11, 0.2)';
    return 'rgba(239, 68, 68, 0.2)';
  }};
  color: ${props => {
    if (props.$workflowId === 1) return 'var(--info)';
    if (props.$workflowId === 2) return 'var(--success)';
    if (props.$workflowId === 3) return 'var(--warning)';
    return 'var(--error)';
  }};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
`;

const TaskItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  margin-bottom: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface);
    border-color: var(--primary);
  }
`;

const StartWorkflowButton = styled.button`
  width: 100%;
  padding: 12px;
  background: ${props => {
    if (props.$workflowId === 1) return 'linear-gradient(135deg, var(--info), var(--info))';
    if (props.$workflowId === 2) return 'linear-gradient(135deg, var(--success), var(--success))';
    if (props.$workflowId === 3) return 'linear-gradient(135deg, var(--warning), var(--warning))';
    return 'linear-gradient(135deg, var(--error), var(--error))';
  }};
  border: none;
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  margin: 12px 16px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const WorkflowSequence = styled.div`
  margin-top: auto;
  padding: 16px;
  background: var(--bg);
  border-top: 1px solid var(--card-border);
`;

const SequenceHeader = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SequenceStep = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  margin-bottom: 8px;
  border-left: 3px solid ${props => {
    if (props.$status === 'completed') return 'var(--success)';
    if (props.$status === 'running') return 'var(--warning)';
    return 'var(--text-muted)';
  }};
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface);
    border-color: var(--primary);
    transform: translateX(4px);
  }
`;

const SequenceStepIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    if (props.$status === 'completed') return 'rgba(16, 185, 129, 0.2)';
    if (props.$status === 'running') return 'rgba(245, 158, 11, 0.2)';
    return 'rgba(100, 116, 139, 0.2)';
  }};
  color: ${props => {
    if (props.$status === 'completed') return 'var(--success)';
    if (props.$status === 'running') return 'var(--warning)';
    return 'var(--text-muted)';
  }};
  font-size: 16px;
  flex-shrink: 0;
`;

const SequenceStepContent = styled.div`
  flex: 1;
`;

const SequenceStepTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$status === 'waiting' ? '#888' : 'var(--btn-text)'};
  margin-bottom: 2px;
`;

const SequenceStepMeta = styled.div`
  font-size: 11px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SequenceStepStatus = styled.div`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    if (props.$status === 'completed') return 'rgba(16, 185, 129, 0.2)';
    if (props.$status === 'running') return 'rgba(245, 158, 11, 0.2)';
    return 'rgba(100, 116, 139, 0.2)';
  }};
  color: ${props => {
    if (props.$status === 'completed') return 'var(--success)';
    if (props.$status === 'running') return 'var(--warning)';
    return '#888';
  }};
  white-space: nowrap;
`;

const DeleteStepButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: 0.5;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    color: var(--error);
    opacity: 1;
  }

  &:disabled {
    opacity: 0.2;
    cursor: not-allowed;
  }
`;

const StopButton = styled.button`
  background: var(--bg);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 8px 14px;
  color: var(--error);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(242, 63, 67, 0.15);
    border-color: var(--error);
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const WorkflowsColumned = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProcesses, setActiveProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [collapsedWorkflows, setCollapsedWorkflows] = useState({});
  const [showAddToQueueModal, setShowAddToQueueModal] = useState(false);
  const [selectedWorkflowForQueue, setSelectedWorkflowForQueue] = useState(null);
  const [showBuilderForWorkflow, setShowBuilderForWorkflow] = useState(null);
  const [taskQueues, setTaskQueues] = useState({}); // {workflowId: [tasks]}
  const [workflowSequences, setWorkflowSequences] = useState({}); // {workflowId: {sequence: [], currentStep: 0}}
  const [renamingWorkflowId, setRenamingWorkflowId] = useState(null);
  const [renamingName, setRenamingName] = useState('');

  useEffect(() => {
    loadWorkflows();
    loadActiveProcesses();
    loadWorkflowSequences();
    const interval = setInterval(() => {
      loadActiveProcesses();
      loadWorkflowSequences();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const workflowsResponse = await fetch(apiUrl('/api/v2/workflows'));
      const workflowsData = await workflowsResponse.json();
      
      if (Array.isArray(workflowsData)) {
        const formattedWorkflows = workflowsData.map(workflow => {
          const devices = Array.isArray(workflow.devices) ? workflow.devices.map(device => ({
            id: device.device_id,
            account_id: device.account_id,
            name: device.device_name || device.device_id,
            username: device.username,
            active: device.is_online || false,
            is_restarter: device.is_restarter || false
          })) : [];
          
          // Trouver le device restarteur
          const restarterDevice = devices.find(d => d.is_restarter === true);
          const restarterDeviceId = restarterDevice ? restarterDevice.id : workflow.restart_device_id;
          
          console.log(`🔧 Workflow ${workflow.id}: Restarter device ID = ${restarterDeviceId}`, restarterDevice);
          
          return {
            id: workflow.id,
            name: workflow.name || `Workflow ${workflow.id}`,
            status: 'stopped',
            restart_device_id: restarterDeviceId,
            devices: devices,
            stats: {
              interactions: 0,
              success: 0,
              runtime: 0
            }
          };
        });
        
        // Trier par ID croissant pour avoir A (1) à gauche, B (2) au milieu, C (3) à droite
        const sortedWorkflows = [...formattedWorkflows].sort((a, b) => a.id - b.id);
        console.log('📋 Workflows order:', sortedWorkflows.map(w => `${w.id}:${w.name}`).join(', '));
        setWorkflows(sortedWorkflows);
        
        const initialCollapsedState = {};
        formattedWorkflows.forEach(workflow => {
          initialCollapsedState[workflow.id] = false;
        });
        setCollapsedWorkflows(initialCollapsedState);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowSequences = async () => {
    try {
      // Charger les workflows pour avoir leurs IDs
      const workflowsResponse = await fetch(apiUrl('/api/v2/workflows'));
      const workflowsData = await workflowsResponse.json();
      
      const sequences = {};
      
      // Pour chaque workflow, charger les execution steps depuis la DB
      for (const workflow of workflowsData) {
        try {
          const seqResponse = await fetch(apiUrl(`/api/workflow-sequences/${workflow.id}`));
          const seqData = await seqResponse.json();
          
          if (seqData && seqData.execution_steps && Array.isArray(seqData.execution_steps)) {
            // Convertir les execution_steps en format attendu par l'UI
            const stepsForUI = seqData.execution_steps.map(step => ({
              id: step.id,
              scriptType: step.script_type,
              username: step.username,
              deviceId: step.device_id,
              status: step.status,
              pid: step.pid,
              stepIndex: step.step_index,
              startedAt: step.started_at,
              completedAt: step.completed_at
            }));
            
            // Trouver le step en cours (running) ou le premier pending
            const runningStep = seqData.execution_steps.findIndex(s => s.status === 'running');
            const pendingStep = seqData.execution_steps.findIndex(s => s.status === 'pending');
            const currentStep = runningStep >= 0 ? runningStep : (pendingStep >= 0 ? pendingStep : 0);
            
            sequences[workflow.id] = {
              sequence: stepsForUI,
              currentStep: currentStep,
              sequenceId: seqData.id,
              sequenceStatus: seqData.status
            };
            
            console.log(`📋 Workflow ${workflow.id}: ${stepsForUI.length} steps (current: ${currentStep})`);
          }
        } catch (err) {
          // Pas de séquence active pour ce workflow, c'est OK
        }
      }
      
      setWorkflowSequences(sequences);
      console.log('📋 Workflow sequences loaded:', sequences);
    } catch (error) {
      console.error('Erreur lors du chargement des séquences:', error);
    }
  };

  const loadActiveProcesses = async () => {
    try {
      // Récupérer les processus depuis le ProcessMonitor endpoint (port 3002)
      const response = await fetch(apiUrl('/api/processes'));
      const data = await response.json();
      
      console.log('📊 Active processes data:', data);
      
      if (Array.isArray(data.processes)) {
        console.log('✅ Setting active processes:', data.processes.length, 'processes');
        // Mapper les processus avec leur workflow_id
        // ProcessMonitor utilise des lettres (A, B, C) mais on filtre par numéros (1, 2, 3)
        const workflowLetterToId = { 'A': 1, 'B': 2, 'C': 3 };
        
        const processesWithWorkflow = data.processes.map(proc => {
          let workflowId = 1; // Par défaut
          
          // Si proc.workflow est une lettre (A, B, C), convertir en numéro
          if (proc.workflow && typeof proc.workflow === 'string') {
            workflowId = workflowLetterToId[proc.workflow.toUpperCase()] || 1;
          } else if (proc.workflow && typeof proc.workflow === 'number') {
            workflowId = proc.workflow;
          }
          
          console.log(`   - Process @${proc.username} (PID ${proc.pid}) → Workflow ${proc.workflow} → ID ${workflowId}`);
          return {
            ...proc,
            workflow_id: workflowId
          };
        });
        setActiveProcesses(processesWithWorkflow);
      } else {
        console.log('⚠️ No processes array found in response');
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des processus:', error);
    }
  };

  const getProcessesByWorkflow = (workflowId) => {
    const filtered = activeProcesses.filter(p => p.workflow_id === workflowId);
    console.log(`🔍 Workflow ${workflowId}: ${filtered.length} processes (total: ${activeProcesses.length})`);
    return filtered;
  };

  const getScriptIcon = (scriptType) => {
    switch(scriptType) {
      case 'interact': return '❤️';
      case 'unfollow': return '👋';
      case 'follower_analysis': return '📊';
      case 'gmail': case 'create_gmail': case 'detect_gmail': case 'analyze_emails': return '📧';
      case 'maps_review': return '🗺️';
      default: return '🔄';
    }
  };

  const getStepStatus = (workflowId, stepIndex) => {
    const sequence = workflowSequences[workflowId];
    if (!sequence || !sequence.sequence[stepIndex]) return 'waiting';

    // Utiliser le status directement depuis les données DB
    const step = sequence.sequence[stepIndex];
    if (step.status === 'completed') return 'completed';
    if (step.status === 'running') return 'running';
    if (step.status === 'failed') return 'failed';
    return 'waiting'; // pending ou autre
  };

  const toggleWorkflow = (workflowId) => {
    setCollapsedWorkflows(prev => ({
      ...prev,
      [workflowId]: !prev[workflowId]
    }));
  };

  const handleRenameWorkflow = async (workflowId) => {
    const trimmed = renamingName.trim();
    if (!trimmed) { setRenamingWorkflowId(null); return; }
    try {
      const res = await fetch(apiUrl(`/api/v2/workflows/${workflowId}/rename`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed })
      });
      if (res.ok) {
        setWorkflows(prev => prev.map(w => w.id === workflowId ? { ...w, name: trimmed } : w));
      }
    } catch (err) {
      console.error('Erreur renommage workflow:', err);
    }
    setRenamingWorkflowId(null);
  };

  const handleAddToQueue = (queueItems) => {
    console.log('Ajout à la queue:', queueItems);
    
    if (selectedWorkflowForQueue) {
      const workflowId = selectedWorkflowForQueue.id;
      setTaskQueues(prev => ({
        ...prev,
        [workflowId]: [...(prev[workflowId] || []), ...queueItems]
      }));
    }
    
    setShowAddToQueueModal(false);
    setSelectedWorkflowForQueue(null);
  };

  const handleStartWorkflow = async (workflowId) => {
    const tasks = taskQueues[workflowId] || [];
    
    if (tasks.length === 0) {
      alert('Aucune tâche dans la queue. Ajoutez des tâches avant de démarrer le workflow.');
      return;
    }

    try {
      console.log(`🚀 Démarrage du workflow ${workflowId} avec ${tasks.length} tâche(s)`);
      
      // Lancer chaque tâche séquentiellement
      for (const task of tasks) {
        console.log(`   - Lancement de ${task.scriptType} pour @${task.deviceUsername}`);
        
        let endpoint = '';
        if (task.scriptType === 'interact') {
          endpoint = `/api/start-interaction/${task.accountId}`;
        } else if (task.scriptType === 'unfollow') {
          endpoint = `/api/start-unfollow/${task.accountId}`;
        }
        
        if (endpoint) {
          await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ withRestarter: true })
          });
          
          // Attendre 3 secondes entre chaque lancement
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
      
      // Vider la queue après le lancement
      setTaskQueues(prev => ({
        ...prev,
        [workflowId]: []
      }));
      
      alert(`✅ Workflow ${workflowId} démarré avec succès !`);
    } catch (error) {
      console.error('Erreur lors du démarrage du workflow:', error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  const handleRemoveTask = (workflowId, taskIndex) => {
    setTaskQueues(prev => ({
      ...prev,
      [workflowId]: prev[workflowId].filter((_, idx) => idx !== taskIndex)
    }));
  };

  const handleStopProcess = async (pid, username) => {
    if (!window.confirm(`Arrêter le processus @${username} (PID: ${pid}) ?\n\nLe script recevra un signal Ctrl+C pour se terminer proprement.`)) {
      return;
    }

    try {
      console.log(`🛑 Arrêt du processus PID ${pid} (@${username})`);
      
      const response = await fetch(apiUrl('/api/stop-process'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pid })
      });

      const result = await response.json();

      if (response.ok) {
        alert(`✅ Signal d'arrêt envoyé au processus @${username}`);
        // Recharger les processus après 2 secondes
        setTimeout(loadActiveProcesses, 2000);
      } else {
        alert(`❌ Erreur: ${result.error}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'arrêt du processus:', error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  const handleStopAllWorkflowProcesses = async (workflowId) => {
    const processes = getProcessesByWorkflow(workflowId);
    
    if (processes.length === 0) {
      alert('Aucun processus actif à arrêter.');
      return;
    }

    if (!window.confirm(`Arrêter tous les processus du workflow (${processes.length} processus) et supprimer les tâches en attente ?\n\nChaque script recevra un signal Ctrl+C pour se terminer proprement.`)) {
      return;
    }

    try {
      console.log(`🛑 Arrêt de ${processes.length} processus du workflow ${workflowId}`);
      
      // 1. Arrêter tous les processus actifs
      for (const process of processes) {
        await fetch('/api/stop-process', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pid: process.pid })
        });
        
        // Attendre 500ms entre chaque arrêt
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // 2. Supprimer les tâches pending du workflow
      console.log(`🗑️ Suppression des tâches pending du workflow ${workflowId}`);
      const deleteResponse = await fetch(`/api/workflow-sequences/workflow/${workflowId}/pending-steps`, {
        method: 'DELETE'
      });
      
      if (deleteResponse.ok) {
        const deleteResult = await deleteResponse.json();
        console.log(`✅ ${deleteResult.deleted} tâches pending supprimées`);
        alert(`✅ Signal d'arrêt envoyé à ${processes.length} processus\n🗑️ ${deleteResult.deleted} tâches en attente supprimées`);
      } else {
        alert(`✅ Signal d'arrêt envoyé à ${processes.length} processus`);
      }
      
      // Recharger les processus après 2 secondes
      setTimeout(loadActiveProcesses, 2000);
    } catch (error) {
      console.error('Erreur lors de l\'arrêt des processus:', error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  const handleDeleteStep = async (workflowId, stepId, stepInfo) => {
    if (!window.confirm(`Supprimer cette tâche ?\n\n${stepInfo.scriptType} - @${stepInfo.username || 'N/A'}`)) {
      return;
    }

    try {
      console.log(`🗑️ Suppression du step ${stepId}...`);
      
      const response = await fetch(apiUrl(`/api/workflow-sequences/steps/${stepId}`), {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        console.log(`✅ Step supprimé: ${result.deletedStep.scriptType} - @${result.deletedStep.username}`);
        
        // Mettre à jour l'état local en supprimant le step
        setWorkflowSequences(prev => {
          const updated = { ...prev };
          if (updated[workflowId]) {
            updated[workflowId] = {
              ...updated[workflowId],
              sequence: updated[workflowId].sequence.filter(s => s.id !== stepId)
            };
          }
          return updated;
        });
      } else {
        alert(`❌ Erreur: ${result.error}`);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du step:', error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  const handleRestartConnection = async (workflowId) => {
    const workflow = workflows.find(w => w.id === workflowId);
    if (!workflow || !workflow.restart_device_id) {
      alert('❌ Aucun device restarteur configuré pour ce workflow');
      return;
    }

    try {
      console.log(`🔄 Lancement du restart de connexion pour workflow ${workflowId} (device: ${workflow.restart_device_id})`);
      
      const response = await fetch(apiUrl('/api/restart-device'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          deviceId: workflow.restart_device_id,
          workflow_id: workflowId
        })
      });

      const result = await response.json();
      
      if (result.success) {
        console.log(`✅ Restart lancé pour workflow ${workflowId}`);
        // Recharger les processus après 1 seconde
        setTimeout(loadActiveProcesses, 1000);
      } else {
        alert(`❌ Erreur: ${result.error}`);
      }
    } catch (error) {
      console.error('Erreur lors du restart:', error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  const handleSaveWorkflowSequence = async (workflowId, sequenceData) => {
    try {
      const { sequence, mode = 'launch', scheduledDate, scheduledTime } = sequenceData;
      
      if (mode === 'append') {
        // Ajouter à la séquence existante en DB
        const response = await fetch(apiUrl('/api/workflow-sequences'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workflowId,
            sequence,
            mode: 'append'
          })
        });

        if (response.ok) {
          alert('✅ Actions ajoutées à la séquence en cours !');
          setShowBuilderForWorkflow(null);
          loadWorkflowSequences(); // Recharger les séquences
        } else {
          alert('❌ Erreur lors de l\'ajout à la séquence');
        }
      } else if (mode === 'schedule') {
        // Programmer pour plus tard
        const response = await fetch(apiUrl('/api/workflow-sequences'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workflowId,
            sequence,
            mode: 'create',
            scheduledDate,
            scheduledTime
          })
        });

        if (response.ok) {
          alert(`📅 Workflow programmé pour le ${scheduledDate} à ${scheduledTime}`);
          setShowBuilderForWorkflow(null);
        } else {
          alert('❌ Erreur lors de la programmation');
        }
      } else {
        // Lancer maintenant (mode 'launch')
        // Créer une nouvelle séquence et la démarrer immédiatement
        const createResponse = await fetch(apiUrl('/api/workflow-sequences'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workflowId,
            sequence,
            mode: 'create'
          })
        });

        if (createResponse.ok) {
          const { sequenceId, stepsCreated } = await createResponse.json();
          console.log(`✅ Séquence créée: ${sequenceId} avec ${stepsCreated} steps`);
          
          // Récupérer tous les steps de la DB
          const stepsResponse = await fetch(apiUrl(`/api/workflow-sequences/${sequenceId}/steps`));
          const stepsData = await stepsResponse.json();
          
          if (!stepsData.success || !stepsData.steps) {
            alert('❌ Erreur: Impossible de récupérer les steps');
            return;
          }
          
          const steps = stepsData.steps;
          console.log(`📋 ${steps.length} steps à exécuter:`, steps);
          
          // Lancer uniquement le PREMIER step, le Workflow Monitor gère le reste
          if (steps.length > 0) {
            const firstStep = steps[0];
            console.log(`\n🔹 Lancement du premier step: ${firstStep.script_type} - @${firstStep.username}`);
            
            // Marquer le premier step comme démarré
            await fetch(apiUrl(`/api/workflow-sequences/steps/${firstStep.id}/start`), {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ pid: null })
            });
            
            // Si c'est un restart, appeler l'API de restart
            if (firstStep.script_type === 'restart') {
              console.log(`   🔄 Exécution du restart pour device: ${firstStep.device_id}`);
              console.log(`   🔍 First step data:`, firstStep);
              
              if (!firstStep.device_id) {
                console.error(`   ❌ ERREUR: device_id est vide!`, firstStep);
                alert(`❌ Erreur: Le device_id du restarteur est vide. Vérifiez la configuration du workflow.`);
                return;
              }
              
              try {
                const requestBody = { 
                  deviceId: firstStep.device_id,
                  workflow_id: workflowId  // Pour afficher dans le bon workflow
                };
                console.log(`   📤 Envoi requête restart:`, requestBody);
                
                const restartResponse = await fetch(apiUrl('/api/restart-device'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(requestBody)
                });
                
                const restartResult = await restartResponse.json();
                console.log(`   📤 Résultat restart:`, restartResult);
                
                if (restartResult.success) {
                  // NE PAS marquer comme completed ici - le Workflow Monitor détectera la fin du script
                  // et marquera le step comme completed quand le terminal se fermera
                  console.log(`   ✅ Restart lancé (status: running), le Workflow Monitor détectera la fin`);
                } else {
                  throw new Error(restartResult.error || 'Restart failed');
                }
              } catch (error) {
                console.error(`   ❌ Erreur restart:`, error);
                alert(`❌ Erreur lors du restart: ${error.message}`);
                return;
              }
            } else {
              // Lancer le script normalement
              let endpoint = '';
              if (firstStep.script_type === 'interact') {
                endpoint = apiUrl(`/api/start-interaction/${firstStep.account_id}`);
              } else if (firstStep.script_type === 'unfollow') {
                endpoint = apiUrl(`/api/start-unfollow/${firstStep.account_id}`);
              }
            
              if (endpoint) {
                console.log(`   🎯 Lancement: ${endpoint}`);
                
                try {
                  const launchResponse = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ withRestarter: true })
                  });
                  
                  const launchResult = await launchResponse.json();
                  console.log(`   📤 Résultat:`, launchResult);
                  console.log(`   ✅ Premier script lancé, le Workflow Monitor va gérer la suite`);
                  
                } catch (error) {
                  console.error(`   ❌ Erreur lors du lancement:`, error);
                  alert(`❌ Erreur lors du lancement: ${error.message}`);
                  return;
                }
              }
            }
          }
          
          alert(`✅ Séquence lancée ! Le Workflow Monitor va exécuter les ${steps.length} step(s) automatiquement.`);
          setShowBuilderForWorkflow(null);
          loadWorkflowSequences();
        } else {
          alert('❌ Erreur lors de la création de la séquence');
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <SharedPageHeader icon={Zap} title="Workflows" subtitle="Automatisation des tâches" />
        <EmptyState>
          <EmptyText>Chargement des workflows...</EmptyText>
        </EmptyState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <SharedPageHeader icon={Zap} title="Workflows" subtitle="Automatisation des tâches" />

      <WorkflowsContainer>
        {workflows.map(workflow => {
          const processes = getProcessesByWorkflow(workflow.id);
          
          return (
            <WorkflowColumn key={workflow.id} $workflowId={workflow.id}>
              <ColumnHeader 
                $workflowId={workflow.id}
                onClick={() => toggleWorkflow(workflow.id)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ flex: 1 }}>
                  <ColumnTitle>
                    <WorkflowIcon $workflowId={workflow.id}>
                      {workflow.id}
                    </WorkflowIcon>
                    {renamingWorkflowId === workflow.id ? (
                      <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <input
                          autoFocus
                          value={renamingName}
                          onChange={e => setRenamingName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRenameWorkflow(workflow.id); if (e.key === 'Escape') setRenamingWorkflowId(null); }}
                          style={{ background: 'var(--bg)', border: '1px solid var(--primary)', borderRadius: 6, padding: '4px 8px', color: 'var(--text-primary)', fontSize: 15, fontWeight: 700, width: 160, outline: 'none' }}
                        />
                        <button onClick={() => handleRenameWorkflow(workflow.id)} style={{ background: 'none', border: 'none', color: 'var(--success)', cursor: 'pointer', padding: 2 }}><Check size={16} /></button>
                        <button onClick={() => setRenamingWorkflowId(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}><X size={16} /></button>
                      </span>
                    ) : workflow.name}
                  </ColumnTitle>
                  <ColumnMeta>
                    <Smartphone size={14} />
                    {workflow.devices.length} devices • {processes.length} processus actif{processes.length > 1 ? 's' : ''}
                  </ColumnMeta>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                  {processes.length > 0 && (
                    <StopButton
                      onClick={() => handleStopAllWorkflowProcesses(workflow.id)}
                      title="Arrêter tous les processus"
                    >
                      <StopCircle size={16} />
                      Stop
                    </StopButton>
                  )}
                  <ActionButton
                    onClick={() => handleRestartConnection(workflow.id)}
                    title="Restart connexion"
                    $workflowId={workflow.id}
                  >
                    <RefreshCw size={16} />
                  </ActionButton>
                  <ActionButton
                    onClick={() => { setRenamingWorkflowId(workflow.id); setRenamingName(workflow.name); }}
                    title="Renommer"
                    $workflowId={workflow.id}
                  >
                    <Edit3 size={16} />
                  </ActionButton>
                  <ActionButton
                    onClick={() => setShowBuilderForWorkflow(showBuilderForWorkflow === workflow.id ? null : workflow.id)}
                    title="Créer une séquence"
                    $workflowId={workflow.id}
                  >
                    <Settings size={16} />
                  </ActionButton>
                  <ExpandButton>
                    {collapsedWorkflows[workflow.id] ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                  </ExpandButton>
                </div>
              </ColumnHeader>

              {/* Task Queue */}
              {taskQueues[workflow.id] && taskQueues[workflow.id].length > 0 && (
                <>
                  <TaskQueue>
                    <TaskQueueHeader>
                      <TaskQueueTitle>
                        📋 File d'attente
                        <TaskQueueCount $workflowId={workflow.id}>
                          {taskQueues[workflow.id].length}
                        </TaskQueueCount>
                      </TaskQueueTitle>
                    </TaskQueueHeader>
                    {taskQueues[workflow.id].map((task, idx) => (
                      <TaskItem key={idx}>
                        <span>
                          {task.scriptType === 'interact' && '❤️'}
                          {task.scriptType === 'unfollow' && '👋'}
                          {task.scriptType === 'follower_analysis' && '📊'}
                          {task.scriptType === 'gmail' && '📧'}
                        </span>
                        <span>@{task.deviceUsername}</span>
                        <span style={{ marginLeft: 'auto', cursor: 'pointer' }} onClick={() => handleRemoveTask(workflow.id, idx)}>
                          ✕
                        </span>
                      </TaskItem>
                    ))}
                  </TaskQueue>
                  <StartWorkflowButton 
                    $workflowId={workflow.id}
                    onClick={() => handleStartWorkflow(workflow.id)}
                  >
                    <Play size={16} />
                    Démarrer le workflow ({taskQueues[workflow.id].length} tâche{taskQueues[workflow.id].length > 1 ? 's' : ''})
                  </StartWorkflowButton>
                </>
              )}

              <ProcessesList>
                {processes.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon>
                      <Zap size={28} />
                    </EmptyIcon>
                    <EmptyText>Aucun processus actif</EmptyText>
                  </EmptyState>
                ) : (
                  processes.map(process => {
                    const isSelected = selectedProcess?.pid === process.pid;
                    
                    return (
                      <ProcessCard
                        key={process.pid}
                        $workflowId={workflow.id}
                        $selected={isSelected}
                        onClick={() => setSelectedProcess(isSelected ? null : process)}
                      >
                        <ProcessHeader>
                          <ProcessProfilePic>
                            <img 
                              src={getLocalProfilePicUrl(process.username)}
                              alt={process.username}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="fallback" style={{display: 'none'}}>
                              {process.username ? process.username.charAt(0).toUpperCase() : '?'}
                            </div>
                          </ProcessProfilePic>
                          <ProcessInfo>
                            <ProcessUsername>@{process.username}</ProcessUsername>
                            <ProcessDevice>
                              {process.device_name || process.device_id || 'N/A'} • PID: {process.pid}
                            </ProcessDevice>
                          </ProcessInfo>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <StopButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStopProcess(process.pid, process.username);
                              }}
                              title="Arrêter ce processus"
                            >
                              <StopCircle size={14} />
                            </StopButton>
                            <ProcessStatus $status="running">
                              <Play size={12} />
                              En cours
                            </ProcessStatus>
                          </div>
                        </ProcessHeader>

                        <ProcessProgress>
                          <ProgressText>
                            <span style={{ fontSize: '15px', fontWeight: '600' }}>
                              {(() => {
                                const st = process.script_type || process.scriptType || '';
                                if (st === 'interact') return '❤️ Interact';
                                if (st === 'unfollow') return '👋 Unfollow';
                                if (st === 'follower_analysis') return '📊 Analysis';
                                if (st === 'create_gmail') return '📧 Gmail — Création';
                                if (st === 'detect_gmail') return '📧 Gmail — Détection';
                                if (st === 'analyze_emails') return '📧 Gmail — Analyse';
                                if (st === 'gmail') return '📧 Gmail';
                                if (st === 'maps_review') return '🗺️ Google Maps';
                                return '🔄 Running';
                              })()}
                            </span>
                            <span style={{ fontSize: '12px' }}>
                              CPU: {process.cpu_percent}% • RAM: {process.memory_mb}MB
                            </span>
                          </ProgressText>
                          {process.target && (
                            <ProgressText style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
                              <span>🎯 Target: @{process.target}</span>
                              <span>⏱️ {process.running_time}</span>
                            </ProgressText>
                          )}
                        </ProcessProgress>
                      </ProcessCard>
                    );
                  })
                )}
              </ProcessesList>

              {/* Workflow Sequence */}
              {workflowSequences[workflow.id] && workflowSequences[workflow.id].sequence.length > 0 && (
                <WorkflowSequence>
                  <SequenceHeader>
                    🎯 Séquence du workflow
                  </SequenceHeader>
                  {workflowSequences[workflow.id].sequence.map((step, idx) => {
                    const status = getStepStatus(workflow.id, idx);
                    return (
                      <SequenceStep key={idx} $status={status}>
                        <SequenceStepIcon $status={status}>
                          {getScriptIcon(step.scriptType)}
                        </SequenceStepIcon>
                        <SequenceStepContent>
                          <SequenceStepTitle $status={status}>
                            {step.scriptType === 'interact' && 'Interaction'}
                            {step.scriptType === 'unfollow' && 'Unfollow'}
                            {step.scriptType === 'follower_analysis' && 'Analyse des followers'}
                            {step.scriptType === 'gmail' && '📧 Gmail'}
                            {step.scriptType === 'restart' && '🔄 Restart'}
                            {!['interact', 'unfollow', 'follower_analysis', 'restart', 'gmail'].includes(step.scriptType) && step.scriptType}
                            {step.username && step.username !== 'restarter' && ` - @${step.username}`}
                          </SequenceStepTitle>
                          <SequenceStepMeta>
                            <span>Étape {idx + 1}/{workflowSequences[workflow.id].sequence.length}</span>
                            {step.deviceId && <span>• 📱 {step.deviceId}</span>}
                            {step.startedAt && (
                              <span>• 🕐 {new Date(step.startedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                            )}
                            {step.completedAt && (
                              <span>→ {new Date(step.completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                            )}
                          </SequenceStepMeta>
                        </SequenceStepContent>
                        <SequenceStepStatus $status={status}>
                          {status === 'completed' && '✓ Terminé'}
                          {status === 'running' && '▶ En cours'}
                          {status === 'waiting' && '⏳ En attente'}
                          {status === 'failed' && '❌ Échoué'}
                        </SequenceStepStatus>
                        {step.id && status !== 'running' && (
                          <DeleteStepButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteStep(workflow.id, step.id, step);
                            }}
                            title="Supprimer cette tâche"
                          >
                            <Trash2 size={14} />
                          </DeleteStepButton>
                        )}
                      </SequenceStep>
                    );
                  })}
                </WorkflowSequence>
              )}
            </WorkflowColumn>
          );
        })}
      </WorkflowsContainer>

      {showAddToQueueModal && selectedWorkflowForQueue && (
        <AddToQueueModal
          workflow={selectedWorkflowForQueue}
          onClose={() => {
            setShowAddToQueueModal(false);
            setSelectedWorkflowForQueue(null);
          }}
          onAddToQueue={handleAddToQueue}
        />
      )}

      {showBuilderForWorkflow && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={() => setShowBuilderForWorkflow(null)}
        >
          <div 
            style={{
              background: '#1a1d29',
              borderRadius: '16px',
              width: '95%',
              maxWidth: '1600px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <WorkflowBuilder
              workflow={workflows.find(w => w.id === showBuilderForWorkflow)}
              onSave={(sequenceData) => handleSaveWorkflowSequence(showBuilderForWorkflow, sequenceData)}
              onClose={() => setShowBuilderForWorkflow(null)}
              hasRunningProcesses={getProcessesByWorkflow(showBuilderForWorkflow).length > 0}
            />
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default WorkflowsColumned;
